import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { CurrentPortfolioSummary } from "../hooks/useCurrentPortfolio";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import localizedFormat from "dayjs/plugin/localizedFormat";
import PortfolioSummarySkeleton from "./PortfolioSummarySkeleton";
import { getFlatPortfolioColors, portfolioTypography } from "../../../../utils/getFlatPortfolioColors";

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

interface PortfolioSummaryProps {
  summary: CurrentPortfolioSummary | null | undefined;
  isRefetching?: boolean;
  totalPositions?: number;
}

const PortfolioSummary: React.FC<PortfolioSummaryProps> = ({ summary, totalPositions = 0 }) => {
  const theme = useTheme();
  const colors = getFlatPortfolioColors(theme);
  const currencySymbol = "$";
  const currencyCode = "USD";
  
  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return `${currencySymbol}0.00`;
    }
    const locale =
      currencyCode === "USD"
        ? "en-US"
        : currencyCode === "EUR"
        ? "en-EU"
        : "en-GB";
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercentage = (percent: number | null | undefined) => {
    if (percent === null || percent === undefined || isNaN(percent)) {
      return "0.00%";
    }
    const sign = percent >= 0 ? "+" : "";
    return `${sign}${percent.toFixed(2)}%`;
  };

  // Get functional color based on value (gains = green, losses = red)
  const getValueColor = (value: number | null | undefined) => {
    if (value === null || value === undefined) return colors.text.primary;
    return value >= 0 ? colors.functional.success : colors.functional.error;
  };

  if (!summary) {
    return <PortfolioSummarySkeleton />;
  }

  return (
    <Box
      sx={{
        backgroundColor: colors.background.secondary,
        border: `1px solid ${colors.border.primary}`,
        borderRadius: 0, // Sharp corners - no rounding
        padding: 3,
        marginBottom: 3,
        // NO elevation, NO shadows, NO gradients - pure flat design
      }}
    >
      {/* Primary Metric Row - Hero Section (Old Layout) */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start", // Align to top for better layout
          position: "relative",
        }}
      >
        {/* Left Side - Portfolio Value and Today's Change */}
        <Box>
          <Typography
            sx={{
              ...portfolioTypography.label,
              color: colors.text.muted,
              marginBottom: 1,
            }}
          >
            TOTAL PORTFOLIO VALUE
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, marginBottom: 2 }}>
            <Typography
              sx={{
                fontSize: { xs: "2.5rem", md: "3.5rem" }, // Bigger text as requested
                fontWeight: 700,
                lineHeight: 1,
                letterSpacing: "-0.02em",
                color: colors.text.primary,
              }}
            >
              {formatCurrency(summary.totalValue)}
            </Typography>
            
            {/* Today's Change - Next to portfolio value */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: { xs: "1rem", md: "1.25rem" },
                  fontWeight: 700,
                  color: getValueColor(summary.dayChange),
                  lineHeight: 1.2,
                }}
              >
                {summary.dayChange >= 0 ? "+" : ""}{formatCurrency(summary.dayChange)}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: getValueColor(summary.dayChange),
                }}
              >
                {summary.dayChange >= 0 ? "▲" : "▼"}
                {formatPercentage(summary.dayChangePercent)}
              </Typography>
            </Box>
          </Box>
          
          {/* Total Invested - Below portfolio value */}
          <Box>
            <Typography
              sx={{
                ...portfolioTypography.label,
                color: colors.text.muted,
                marginBottom: 0.5,
              }}
            >
              TOTAL INVESTED
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "1rem", md: "1.25rem" },
                fontWeight: 600,
                color: colors.text.primary,
                lineHeight: 1.2,
              }}
            >
              {formatCurrency(summary.totalCost)}
            </Typography>
          </Box>
        </Box>

        {/* Right Side - Total Gain/Loss */}
        <Box sx={{ textAlign: "right" }}>
          <Typography
            sx={{
              ...portfolioTypography.label,
              color: colors.text.muted,
              marginBottom: 1,
            }}
          >
            TOTAL GAIN/LOSS
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "1.5rem", md: "2rem" },
              fontWeight: 700,
              color: getValueColor(summary.totalReturn),
              lineHeight: 1.2,
              marginBottom: 1,
            }}
          >
            {summary.totalReturn >= 0 ? "+" : ""}{formatCurrency(summary.totalReturn)}
          </Typography>
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 600,
              color: getValueColor(summary.totalReturn),
              textAlign: "right",
            }}
          >
            {summary.totalReturn >= 0 ? "▲" : "▼"}
            {formatPercentage(summary.totalReturnPercent)}
          </Typography>
        </Box>
      </Box>


    </Box>
  );
};

export default PortfolioSummary;
